import * as swcHelpers from '@swc/helpers';
import { DataLayer, calcPrice } from '@grandvisionhq/analytics';
import { Mappers } from '@grandvisionhq/carts-v2';
import { mapOrderLineItemToDataLayerProductObjects } from '@grandvisionhq/fun-orders';
import { ExpressPaymentMethod } from '@grandvisionhq/payments';
import { monetate } from '@grandvisionhq/svc-monetate';
import { optionalKey } from '@grandvisionhq/utils';

var CheckoutSteps;
(function(CheckoutSteps) {
    CheckoutSteps["Login"] = 'login';
    CheckoutSteps["Shipping"] = 'shipping';
    CheckoutSteps["Overview"] = 'overview';
    CheckoutSteps["Payment"] = 'payment';
})(CheckoutSteps || (CheckoutSteps = {}));
const checkoutSteps = {
    [CheckoutSteps.Login]: 1,
    [CheckoutSteps.Shipping]: 2,
    [CheckoutSteps.Overview]: 3,
    [CheckoutSteps.Payment]: 4
};
const pushCheckoutEventToDataLayer = (cart, currentStep, currency)=>{
    DataLayer.pushEvent({
        event: 'gvCheckout',
        atfEcommerce: {
            ecommerce: {
                currencyCode: currency,
                cart: {
                    id: cart.id
                },
                checkout: {
                    actionField: {
                        step: checkoutSteps[currentStep]
                    },
                    products: cart.lineItems.map((lineItem)=>Mappers.mapClientModelToProductObject(lineItem, lineItem.quantity)
                    )
                }
            }
        }
    });
};
const pushCheckoutNavigationEventToDataLayer = (cart, currentStep, currency)=>{
    DataLayer.pushEvent({
        event: 'Checkout',
        atfEcommerce: {
            ecommerce: {
                currencyCode: currency,
                cart: {
                    id: cart.id
                },
                checkout: {
                    actionField: {
                        step: currentStep
                    },
                    products: cart.lineItems.map((lineItem)=>Mappers.mapClientModelToProductObject(lineItem, lineItem.quantity)
                    )
                }
            }
        }
    });
};
const pushExpressCheckoutEventToDataLayer = (cart, currency)=>{
    DataLayer.pushEvent({
        event: 'gvCheckout',
        atfEcommerce: {
            ecommerce: {
                currencyCode: currency,
                cart: {
                    id: cart.id
                },
                type: 'express checkout',
                checkout: {
                    actionField: {
                        step: 2
                    },
                    products: cart.lineItems.map((lineItem)=>Mappers.mapClientModelToProductObject(lineItem, lineItem.quantity)
                    )
                }
            }
        }
    });
};
const pushPurchaseEventToDataLayer = (order, currency, cartId, expressOrder, donation)=>{
    const { rawLineItems , orderNumber , discountedAmount , totalPrice , shippingMethodPrice , subtotalAmount , taxAmount ,  } = order;
    const discountTotal = discountedAmount;
    const discountCodes = order.discountCodes ? order.discountCodes.map((discountCode)=>discountCode.code
    ).join() : '';
    DataLayer.pushEcommerceEvent({
        event: 'gvPurchase',
        atfEcommerce: {
            ecommerce: {
                currencyCode: currency,
                cart: {
                    id: cartId !== null && cartId !== void 0 ? cartId : ''
                },
                purchase: {
                    actionField: swcHelpers.objectSpread({
                        id: orderNumber,
                        revenue: calcPrice(totalPrice),
                        tax: taxAmount && calcPrice(taxAmount),
                        shipping: calcPrice(shippingMethodPrice),
                        revenue_includes_tax: 'true',
                        original_revenue: calcPrice(subtotalAmount),
                        discount_revenue: calcPrice(discountTotal),
                        payment_method: order.paymentMethodKey,
                        checkout_method: expressOrder ? `express_checkout_${order.paymentMethodKey}` : 'regular_checkout'
                    }, optionalKey('coupon', discountCodes), donation && {
                        donation: calcPrice(donation.centAmount)
                    }),
                    products: mapOrderLineItemToDataLayerProductObjects(rawLineItems)
                }
            }
        }
    });
};
function mapOrderLineItemsToPurchaseRows(rawLineItems, orderNumber, currency) {
    const regularLineItems = rawLineItems.map((lineItem)=>{
        return {
            currency,
            purchaseId: orderNumber,
            productId: lineItem.product.sku,
            sku: lineItem.product.sku,
            quantity: (lineItem === null || lineItem === void 0 ? void 0 : lineItem.quantity) || 1,
            unitPrice: (lineItem.price.unit.gross / 100).toFixed(2)
        };
    });
    return regularLineItems;
}
function trackOrderCompletionEvents(order, currency) {
    const batchEvents = [
        [
            `PaymentType__${order.paymentMethod}`
        ],
        [
            `ShippingMode__${order.deliveryMethod}`
        ], 
    ];
    order === null || order === void 0 ? void 0 : order.lineItems.forEach((lineItem)=>{
        const type = lineItem.productType;
        if (type) {
            batchEvents.push([
                `Product__${type}`
            ]);
        }
    });
    order === null || order === void 0 ? void 0 : order.rawLineItems.forEach((lineItem)=>{
        if ('configurationItems' in lineItem) {
            lineItem.configurationItems.forEach((configurationItem)=>{
                batchEvents.push([
                    `Lenses__${configurationItem.product.sku}`
                ]);
            });
        }
    });
    monetate().trackBatchEvents(...batchEvents);
    monetate().trackPurchases(mapOrderLineItemsToPurchaseRows(order.rawLineItems, order.orderNumber, currency));
}
const pushDonationSelectEventToDataLayer = (foundationKey, interactionType)=>{
    DataLayer.pushEvent({
        event: 'donations_amount_click',
        site_feature: 'donations',
        interaction_type: interactionType ? calcPrice(interactionType) : undefined,
        foundation_key: foundationKey
    });
};
const getInteractionType = (paymentMethod)=>{
    switch(paymentMethod){
        case ExpressPaymentMethod.APPLE_PAY:
            return 'express_applepay_init';
        case ExpressPaymentMethod.PAYPAL:
            return 'express_paypal_init';
        case ExpressPaymentMethod.GOOGLE_PAY:
            return 'express_googlepay_init';
        default:
            return '';
    }
};
const getCategory = (productType, lineItemType)=>{
    switch(lineItemType){
        case 'sunglasses':
            return DataLayer.DataLayerTypes.Category.Sunglasses;
        case 'contact-lenses':
        case 'contact-lenses-subscription':
        case 'contact-lenses-bundle-benelux':
            return DataLayer.DataLayerTypes.Category.ContactLenses;
        case 'solutions':
            return DataLayer.DataLayerTypes.Category.Solutions;
        case 'frames':
        case 'non-prescription-bundle':
        case 'prescription-glasses':
            return productType === 'sunglasses' ? DataLayer.DataLayerTypes.Category.Sunglasses : DataLayer.DataLayerTypes.Category.Optical;
        case 'ready-readers':
            return DataLayer.DataLayerTypes.Category.ReadyReaders;
        case 'accessories':
            return DataLayer.DataLayerTypes.Category.Accessories;
        default:
            return '';
    }
};
const pushExpressCheckoutSubmitEventToDataLayer = ({ cart , paymentMethod , isLoggedIn  })=>{
    const getBrand = (lineItem)=>{
        const brand = lineItem.type == 'prescription-glasses' ? lineItem.frame.brand.name : lineItem.brand.name;
        return brand || '';
    };
    const getSku = (lineItem)=>{
        const sku = lineItem.type == 'prescription-glasses' ? lineItem.frame.sku : lineItem.sku;
        return sku || '';
    };
    DataLayer.pushEvent({
        event: 'express_checkout_confirm_click',
        site_feature: 'express_checkout',
        interaction_type: getInteractionType(paymentMethod),
        session_status: isLoggedIn ? 'logged_in' : 'guest',
        items: cart.lineItems.map((item)=>({
                sku: getSku(item),
                quantity: `${item.quantity}`,
                brand: getBrand(item),
                category: getCategory(item.productType, item.type)
            })
        ) || []
    });
};

export { CheckoutSteps, pushCheckoutEventToDataLayer, pushCheckoutNavigationEventToDataLayer, pushDonationSelectEventToDataLayer, pushExpressCheckoutEventToDataLayer, pushExpressCheckoutSubmitEventToDataLayer, pushPurchaseEventToDataLayer, trackOrderCompletionEvents };
//# sourceMappingURL=data-layer.js.map
